import { useCallback, useRef } from "react";
import useThrottle from "./useThrottle";

/**
 * Hook that enables automatic window scrolling
 * when dragging an element near the top or bottom of the viewport.
 *
 * Useful for drag-and-drop UIs where the user might drag items
 * beyond the visible screen area.
 */
const useDragScrolling = () => {
    // Keeps track of whether auto-scrolling is currently active
    const isScrolling = useRef(false);

    const goDown = useCallback(() => {
        const doc = document.documentElement;
        doc.scrollTop += 6; // Scroll down by 6px per frame

        const { offsetHeight, scrollTop, scrollHeight } = doc;
        const isScrollEnd = offsetHeight + scrollTop >= scrollHeight;

        // Continue scrolling until stopped or until bottom is reached
        if (isScrolling.current && !isScrollEnd) {
            window.requestAnimationFrame(goDown);
        }
    }, []);

    const goUp = useCallback(() => {
        const doc = document.documentElement;
        doc.scrollTop -= 6; // Scroll up by 6px per frame

        // Continue scrolling until stopped or until top is reached
        if (isScrolling.current && window.scrollY > 0) {
            window.requestAnimationFrame(goUp);
        }
    }, []);

    const onDragOver = useCallback((event: DragEvent) => {
        // Detect whether the cursor is near the top or bottom "scroll zones"
        const isMouseOnTop = event.clientY < 100; // Top 100px of the viewport
        const isMouseOnBottom = window.innerHeight - event.clientY < 50; // Bottom 50px

        // Start scrolling if near an edge and not already scrolling
        if (!isScrolling.current && (isMouseOnTop || isMouseOnBottom)) {
            isScrolling.current = true;

            if (isMouseOnTop) {
                window.requestAnimationFrame(goUp);
            }

            if (isMouseOnBottom) {
                window.requestAnimationFrame(goDown);
            }
        }
        // Stop scrolling if cursor moves away from scroll zones
        else if (!isMouseOnTop && !isMouseOnBottom) {
            isScrolling.current = false;
        }
    }, [goUp, goDown]);

    //Drag events can fire many times per second, so throttling
    const throttleOnDragOver = useThrottle(onDragOver, 300);

    /**
     * Starts listening for drag events on the window.
     * Should be called when a drag operation begins.
     */
    const addEventListenerForWindow = () => {
        window.addEventListener("dragover", throttleOnDragOver, false);
    };

    /**
     * Stops listening for drag events and cancels ongoing scrolling.
     * Should be called when a drag operation ends or is cancelled.
     */
    const removeEventListenerForWindow = () => {
        window.removeEventListener("dragover", throttleOnDragOver, false);
        isScrolling.current = false;
    };

    return {
        addEventListenerForWindow,
        removeEventListenerForWindow,
    };
};

export default useDragScrolling;
